import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { ViewContext } from 'components/lib';

interface EditableTextProps {
  text: string;
  onUpdate: (text: string) => void;
  navigateTo: () => void;
  className?: string;
  stopPropagationOnClick?: boolean;
}

function EditableText({ text, onUpdate, navigateTo, stopPropagationOnClick }: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentText, setCurrentText] = useState(text);
  const [clickCount, setClickCount] = useState(0);
  const context = useContext(ViewContext);

  useEffect(() => {
    if (clickCount === 1) {
      const timer = setTimeout(() => {
        navigateTo();
        setClickCount(0);
      }, 200);

      return () => clearTimeout(timer);
    } else if (clickCount === 2) {
      setIsEditing(true);
      setClickCount(0);
    }
    return undefined;
  }, [clickCount, navigateTo]);

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (stopPropagationOnClick) {
      event.stopPropagation();
    }
    setClickCount((prev) => prev + 1);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentText(e.target.value);
  };

  const handleBlur = () => {
    const nextText = currentText.trim();
    setIsEditing(false);
    if (!nextText) {
      setCurrentText(text);
      context.notification.show('New name must no be emtpy', 'error', true, 'toast');
      return;
    }
    if (nextText !== text) {
      setCurrentText(nextText);
      onUpdate(nextText);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleBlur();
    }
  };

  return (
    <>
      {isEditing ? (
        <input
          type="text"
          value={currentText}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={stopPropagationOnClick ? (event) => event.stopPropagation() : undefined}
          className="editable-text-input"
          autoFocus
        />
      ) : (
        <span onClick={handleClick} style={{ cursor: 'text' }}>
          {text}
        </span>
      )}
    </>
  );
}

EditableText.propTypes = {
  text: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  className: PropTypes.string,
  stopPropagationOnClick: PropTypes.bool,
};

export default EditableText;
